import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { AlertCircle, Loader2, RefreshCcw } from 'lucide-react';
import { emitResumeNotice, RESUME_NOTICE_ACTION_EVENT } from '../lib/resumeNotice';
import {
  persistVideoSample,
  restoreYouTubePosition,
  startVideoOver,
} from '../lib/videoProgress';
import { readYouTubeProgress } from '../lib/progressCapture';

const CAPTURE_INTERVAL_MS = 15000;
const API_TIMEOUT_MS = 10000;
let apiPromise = null;

const loadYouTubeIframeApi = () => {
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (apiPromise) return apiPromise;

  apiPromise = new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => reject(new Error('YouTube player timed out')), API_TIMEOUT_MS);
    const previousReady = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      window.clearTimeout(timeout);
      previousReady?.();
      resolve(window.YT);
    };

    if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
      const script = document.createElement('script');
      script.src = 'https://www.youtube.com/iframe_api';
      script.async = true;
      script.onerror = () => {
        window.clearTimeout(timeout);
        apiPromise = null;
        reject(new Error('YouTube player failed to load'));
      };
      document.head.appendChild(script);
    }
  }).catch((error) => {
    apiPromise = null;
    throw error;
  });

  return apiPromise;
};

const buildInlinePlayerOptions = ({
  videoId,
  entity,
  capture,
  restoredKeyRef,
  lastCapturedRef,
  playingRef,
  handoffPauseRef,
  setStatus,
  onPlayingChange,
  onReadyInterval,
  isCancelled = () => false,
}) => ({
  width: '100%',
  height: '100%',
  videoId,
  playerVars: {
    autoplay: 0,
    controls: 1,
    modestbranding: 1,
    playsinline: 1,
    rel: 0,
    origin: window.location.origin,
  },
  events: {
    onReady: (event) => {
      if (isCancelled()) return;
      setStatus('ready');
      const restoreKey = `${entity.type}:${entity.id}`;
      if (restoredKeyRef.current !== restoreKey) {
        const restored = restoreYouTubePosition(event.target, entity);
        restoredKeyRef.current = restoreKey;
        if (restored > 0) {
          lastCapturedRef.current = restored;
          emitResumeNotice({ entity, position: restored, kind: 'watching' });
        }
      }
      onReadyInterval();
    },
    onStateChange: (event) => {
      const state = event.data;
      if (state === window.YT.PlayerState.PLAYING) {
        playingRef.current = true;
        setStatus('playing');
        onPlayingChange?.(true);
      } else if (state === window.YT.PlayerState.BUFFERING) {
        setStatus('buffering');
      } else if (state === window.YT.PlayerState.PAUSED) {
        playingRef.current = false;
        setStatus('paused');
        onPlayingChange?.(false);
        if (handoffPauseRef.current) {
          handoffPauseRef.current = false;
        } else {
          capture(true);
        }
      } else if (state === window.YT.PlayerState.ENDED) {
        playingRef.current = false;
        setStatus('ended');
        onPlayingChange?.(false);
        capture(true);
      }
    },
    onError: () => setStatus('error'),
  },
});

const InlineYouTubePlayer = forwardRef(({ videoId, title, entity, metadata, fallbackUrl, onPlayingChange }, ref) => {
  const containerId = useMemo(() => `inline-youtube-${String(videoId).replace(/[^a-zA-Z0-9_-]/g, '')}`, [videoId]);
  const playerRef = useRef(null);
  const restoredKeyRef = useRef(null);
  const lastCapturedRef = useRef(0);
  const playingRef = useRef(false);
  const handoffPauseRef = useRef(false);
  const [status, setStatus] = useState('loading');
  const [retryKey, setRetryKey] = useState(0);

  const capture = useCallback((force = false) => {
    const sample = readYouTubeProgress(playerRef.current);
    if (!sample || (!force && sample.position <= lastCapturedRef.current)) return null;
    const result = persistVideoSample({
      entity,
      positionSeconds: sample.position,
      durationSeconds: sample.duration,
      metadata: { ...metadata, source: 'inline-youtube-player' },
    });
    if (result.saved) lastCapturedRef.current = sample.position;
    return result;
  }, [entity, metadata]);

  useImperativeHandle(ref, () => ({
    takeHandoffSnapshot: () => {
      const player = playerRef.current;
      const sample = readYouTubeProgress(player) || {
        position: lastCapturedRef.current,
        duration: 0,
      };
      const wasPlaying = playingRef.current;
      capture(true);
      handoffPauseRef.current = true;
      try { player?.pauseVideo?.(); } catch {}
      return { ...sample, wasPlaying };
    },
  }), [capture]);

  useEffect(() => {
    let cancelled = false;
    let intervalId = null;
    setStatus('loading');

    loadYouTubeIframeApi()
      .then(() => {
        if (cancelled) return;
        const options = buildInlinePlayerOptions({
          videoId,
          entity,
          capture,
          restoredKeyRef,
          lastCapturedRef,
          playingRef,
          handoffPauseRef,
          setStatus,
          onPlayingChange,
          isCancelled: () => cancelled,
          onReadyInterval: () => {
            intervalId = window.setInterval(() => capture(), CAPTURE_INTERVAL_MS);
          },
        });
        playerRef.current = new window.YT.Player(containerId, options);
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });

    const handlePageHide = () => capture(true);
    window.addEventListener('pagehide', handlePageHide);
    return () => {
      cancelled = true;
      if (intervalId) window.clearInterval(intervalId);
      window.removeEventListener('pagehide', handlePageHide);
      capture(true);
      try { playerRef.current?.destroy?.(); } catch {}
      playerRef.current = null;
    };
  }, [capture, containerId, entity, onPlayingChange, retryKey, videoId]);

  useEffect(() => {
    const handleAction = (event) => {
      const { notice, action } = event.detail || {};
      if (action !== 'start-over' || notice?.id !== `${entity.type}:${entity.id}`) return;
      startVideoOver({ player: playerRef.current, entity });
      lastCapturedRef.current = 0;
      restoredKeyRef.current = `${entity.type}:${entity.id}`;
    };
    window.addEventListener(RESUME_NOTICE_ACTION_EVENT, handleAction);
    return () => window.removeEventListener(RESUME_NOTICE_ACTION_EVENT, handleAction);
  }, [entity]);

  const retry = () => {
    apiPromise = null;
    restoredKeyRef.current = null;
    setRetryKey((value) => value + 1);
  };

  return (
    <div className="relative aspect-video bg-black rounded-sm overflow-hidden" data-testid="inline-youtube-player">
      <div id={containerId} className="w-full h-full" title={title} />
      {(status === 'loading' || status === 'buffering') && (
        <div className="absolute inset-0 grid place-items-center bg-black/75 pointer-events-none" role="status" aria-live="polite">
          <div className="flex items-center gap-2 text-sm text-white/80">
            <Loader2 className="w-5 h-5 animate-spin text-primary" />
            {status === 'buffering' ? 'Buffering video…' : 'Loading video…'}
          </div>
        </div>
      )}
      {status === 'error' && (
        <div className="absolute inset-0 grid place-items-center bg-black/90 px-6 text-center" role="alert">
          <div>
            <AlertCircle className="w-8 h-8 text-primary mx-auto mb-3" />
            <p className="font-semibold">The inline player could not start.</p>
            <div className="flex flex-wrap justify-center gap-3 mt-4">
              <button type="button" onClick={retry} className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-black text-sm font-bold rounded-sm" data-testid="inline-player-retry">
                <RefreshCcw className="w-4 h-4" /> Retry
              </button>
              <a href={fallbackUrl} target="_blank" rel="noopener noreferrer" className="px-4 py-2 border border-white/20 text-sm rounded-sm">
                Open on YouTube
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

InlineYouTubePlayer.displayName = 'InlineYouTubePlayer';

export { API_TIMEOUT_MS, CAPTURE_INTERVAL_MS, buildInlinePlayerOptions, loadYouTubeIframeApi };
export default InlineYouTubePlayer;
